import { Block } from '@aloft/toolkit-sui';

import { forwardRef } from 'react';

import { GuidedTourTooltip } from '../../../components/GuidedTour/GuidedTourTooltip.tsx';
import { selectAppliedSiteAdjDisplay } from '../../../redux/features/selectors/adjustmentSelectors/selectSiteAdj.ts';
import {
  selectCalculatedMarketAdjustmentColorDisplay,
  selectCalculatedMarketAdjustmentDisplay,
  selectMarketAdjustmentLabelDisplay,
} from '../../../redux/features/selectors/calculatedMarketAdjustmentSelectors/selectCalculatedMarketAdjustment.ts';
import { selectFirstOrFewerCompID } from '../../../redux/features/selectors/selectFirstOrFewerCompID.ts';
import { selectSiteValueDisplay } from '../../../redux/features/selectors/selectSiteValue.ts';
import {
  selectSiteValueRatioDisplay,
  selectSubjectSiteValueRatioDisplay,
} from '../../../redux/features/selectors/selectSiteValueRatio.ts';
import { selectUnadjustedSiteCostDisplay } from '../../../redux/features/selectors/selectUnadjustedSiteCost.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { RootState } from '../../../redux/types.ts';
import { ContentBlock } from '../ColumnComponents/ContentBlock.tsx';
import { DetailLineWithValue } from '../ColumnComponents/DetailLineWithValue.tsx';
import { Divider } from '../ColumnComponents/Divider.tsx';
import { TotalLine } from '../ColumnComponents/TotalLine.tsx';
import { PLACEHOLDER } from '../consts.ts';
import { ViewMode } from '../types.ts';
import { SiteDetails } from './Site/SiteDetails.tsx';
import { SubjectSite } from './Site/SubjectSite.tsx';
import { useSiteValueCalculationString } from './utils/useSiteValueCalculationString.ts';

type SiteProps = {
  id: string;
  viewMode: ViewMode;
};

const SiteTotals = forwardRef<HTMLInputElement, SiteProps>(
  ({ id, viewMode }, opinionOfSiteValueRef) => {
    const unadjustedSiteCost = useAppSelector((state: RootState) =>
      selectUnadjustedSiteCostDisplay(state, id),
    );

    const siteAdj = useAppSelector((state: RootState) => selectAppliedSiteAdjDisplay(state, id));
    const marketAdj = useAppSelector((state: RootState) =>
      selectCalculatedMarketAdjustmentColorDisplay(state, id),
    );
    const marketAdjLabel = useAppSelector((state: RootState) =>
      selectMarketAdjustmentLabelDisplay(state, id),
    );

    const unRoundedMarketAdj = useAppSelector((state: RootState) =>
      selectCalculatedMarketAdjustmentDisplay(state, id),
    );

    if (id === 'subject') {
      return (
        <GuidedTourTooltip
          text="Use the site values of the comparable properties to determine your opinion of the subject’s site value."
          tourID="analysisTour"
          tourIndex={8}
          placement="top"
        >
          <SubjectSite ref={opinionOfSiteValueRef} />
        </GuidedTourTooltip>
      );
    }

    if (viewMode === 'adjustments') {
      return (
        <Block paddingTop="0.5rem">
          <DetailLineWithValue
            text="Site (Size, Location, & View)"
            value={siteAdj?.value}
            valueColor={siteAdj?.color}
          />
          <DetailLineWithValue
            text={marketAdjLabel.short}
            value={marketAdj?.value}
            valueColor={marketAdj?.color}
            topBorder
          />
        </Block>
      );
    }

    return (
      <>
        <TotalLine label="Extracted Site" value={unadjustedSiteCost ?? PLACEHOLDER} subTotal />
        <TotalLine label={marketAdjLabel.full} value={unRoundedMarketAdj} subTotal />
      </>
    );
  },
);

export const Site = forwardRef<HTMLInputElement, SiteProps>(
  ({ id, viewMode }, opinionOfSiteValueRef) => {
    const siteValue = useAppSelector((state: RootState) => selectSiteValueDisplay(state, id));
    const secondOrFewerCompID = useAppSelector(selectFirstOrFewerCompID);

    return (
      <>
        <ContentBlock>
          <SiteDetails id={id} />
          <Block minHeight="4.25rem">
            <SiteTotals ref={opinionOfSiteValueRef} id={id} viewMode={viewMode} />
          </Block>
        </ContentBlock>
        <Divider />
        <ContentBlock>
          <GuidedTourTooltip
            text={[
              'Note, the cost quality and effective age affect site value. By adjusting them, you are allocating the sale price of each property between improvements and site.',
              'HINT: if a site value is too high, try upgrading the cost quality level or decreasing the effective age.',
            ]}
            tourID="analysisTour"
            tourIndex={6}
            displayCondition={id === secondOrFewerCompID}
            placement="top"
          >
            {id === 'subject' ? (
              <SubjectTotalLine id={id} siteValue={siteValue} />
            ) : (
              <CompTotalLine id={id} siteValue={siteValue} />
            )}
          </GuidedTourTooltip>
        </ContentBlock>
      </>
    );
  },
);

const SubjectTotalLine = ({ id, siteValue }: { id: string; siteValue: string | undefined }) => {
  const siteValueRatio = useAppSelector((state: RootState) =>
    selectSubjectSiteValueRatioDisplay(state, id),
  );
  return (
    <TotalLine
      label="Site"
      value={siteValue ?? PLACEHOLDER}
      extra={siteValueRatio ?? PLACEHOLDER}
    />
  );
};

const CompTotalLine = ({ id, siteValue }: { id: string; siteValue: string | undefined }) => {
  const siteValueRatio = useAppSelector((state: RootState) =>
    selectSiteValueRatioDisplay(state, id),
  );
  const siteValueCalculation = useSiteValueCalculationString(id);

  return (
    <TotalLine
      label="Site"
      value={siteValue ?? PLACEHOLDER}
      extra={siteValueRatio ?? PLACEHOLDER}
      tooltipContent={[
        siteValueCalculation,
        'Extracted Site Value + Date of Sale Adjustment = Site Value as of the Effective Date',
        'In addition to land value, these site values include grading, drains, landscaping, paving, and utilities.',
      ]}
    />
  );
};
